import React, { useEffect, useMemo, useState, useRef } from "react";
import axios from "axios";
import {
  Container,
  Card,
  CardBody,
  Badge,
  Button
} from "reactstrap";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import { io } from "socket.io-client";

const API = "/api/orders";
const socket = io("/", {
  autoConnect: false
});


const KitchenDashboard = () => {
  document.title = "KDS | Kitchen";
  const user = JSON.parse(localStorage.getItem("user"));
  const modules = user?.modules || ["restaurant"];
  const [orders, setOrders] = useState([]);
  const [dateFilter, setDateFilter] = useState("TODAY");
  const beepRef = useRef(null);
  const token = localStorage.getItem("token");
  const tenantId = localStorage.getItem("tenantId");

  const [viewType, setViewType] = useState(
    modules.length === 1
      ? modules[0].toUpperCase()
      : "RESTAURANT"
  );

  const api = axios.create({
    baseURL: API,
    headers: {
      Authorization: `Bearer ${token}`
    }
  });

  useEffect(() => {
    beepRef.current = new Audio(
      process.env.PUBLIC_URL + "/notification.mp3"
    );
    beepRef.current.preload = "auto";
    beepRef.current.load();

  }, []);


  useEffect(() => {
    if (!token || !tenantId) return;

    socket.auth = { token };
    socket.connect();

    socket.emit("join-tenant", tenantId);

    socket.on("order-updated", fetchOrders);
    socket.on("order-created", fetchOrders);

    socket.on("order-notification", () => {
      if (beepRef.current) {
        beepRef.current.currentTime = 0;
        beepRef.current.play().catch(() => { });
      }

      fetchOrders(); // ✅ instant UI refresh
    });

    return () => {
      socket.off("order-updated");
      socket.off("order-created");
      socket.off("order-notification");
      socket.disconnect();
    };

  }, [token, tenantId]);

  /* ===================== FETCH ORDERS ===================== */
  const fetchOrders = async () => {
    try {
      const res = await api.get("/");
      setOrders(res.data || []);
    } catch (err) {
      console.error("Failed to fetch orders", err?.response?.data || err);
    }
  };

  useEffect(() => {
    fetchOrders();
    const interval = setInterval(fetchOrders, 10000);
    return () => clearInterval(interval);
  }, [tenantId]);

  /* ===================== HELPERS ===================== */
  // safer detection
  /* ===================== HELPERS ===================== */

  const getOrderViewStatus = (items) => {
  if (!items || items.length === 0) return "Waiting";

  const allDelivered = items.every(i => i.status === "Delivered");
  const anyPreparing = items.some(i => i.status === "Preparing");

  if (allDelivered) return "Delivered";
  if (anyPreparing) return "Preparing";
  return "Waiting";
};

  const isRestaurantItem = (item) =>
    item.categoryModel === "MenuCategory";

  const isBarItem = (item) =>
    item.categoryModel === "BarCategory";

  const getNextItemStatus = (status) => {
    if (status === "Waiting") return "Preparing";
    if (status === "Preparing") return "Delivered";
    return status;
  };

  const handleItemStatus = async (orderId, itemIndex, currentStatus) => {
    const nextStatus = getNextItemStatus(currentStatus);

    try {
      await api.put(`/${orderId}/item-status`, {
        itemIndex,
        status: nextStatus,
        tenantId
      });
      setOrders(prev =>
        prev.map(order =>
          order._id === orderId
            ? {
              ...order,
              items: order.items.map((it, idx) =>
                idx === itemIndex ? { ...it, status: nextStatus } : it
              )
            }
            : order
        )
      );
    } catch (err) {
      console.error("Failed to update item status", err);
    }
  };

  /* ===================== FILTERED ORDERS ===================== */
  const filteredOrders = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return orders
      .filter(order => {
        const orderDate = new Date(order.createdAt);
        orderDate.setHours(0, 0, 0, 0);

        if (dateFilter === "TODAY") {
          return orderDate.getTime() === today.getTime();
        } else {
          return orderDate.getTime() < today.getTime();
        }
      })
      .map(order => {
        const items = order.items
          .map((item, originalIndex) => ({
            ...item,
            _originalIndex: originalIndex
          }))
          .filter(item => {
            if (viewType === "RESTAURANT" && modules.includes("restaurant")) {
              return isRestaurantItem(item);
            }
            if (viewType === "BAR" && modules.includes("bar")) {
              return isBarItem(item);
            }
            return false;
          });

        return items.length > 0 ? { ...order, items } : null;
      })
      .filter(Boolean);
  }, [orders, viewType, dateFilter]);

  /* ===================== COUNTS (BASED ON VIEW) ===================== */
  const openCount = useMemo(
    () =>
      filteredOrders.filter(o =>
        ["Waiting", "Preparing", "Ongoing"].includes(o.status)
      ).length,
    [filteredOrders]
  );

  const completedCount = useMemo(
    () =>
      filteredOrders.filter(o => o.status === "Delivered").length,
    [filteredOrders]
  );

  /* ===================== STATUS BADGE ===================== */
  const renderStatusBadge = (status) => {
    const map = {
      Waiting: "secondary",
      Preparing: "primary",
      Ongoing: "warning",
      Delivered: "success"
    };

    return (
      <span style={{ height: "fit-content", padding: "10px" }}
        className={`badge text-uppercase bg-${map[status]}-subtle text-${map[status]}`}
      >
        {status}
      </span>
    );
  };

  /* ===================== RENDER ===================== */
  return (
    <section>

      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="Kitchen Display" pageTitle="Kitchen" />

          {/* ===================== TOP CARD ===================== */}
          <Card className="mb-4">
            <CardBody className="d-flex justify-content-between align-items-center">

              {/* LEFT : TOGGLE */}
              <div className="d-flex gap-2">

                {modules.includes("restaurant") && (
                  <Button
                    color={viewType === "RESTAURANT" ? "primary" : "light"}
                    onClick={() => setViewType("RESTAURANT")}
                  >
                    🍽 Restaurant
                  </Button>
                )}

                {modules.includes("bar") && (
                  <Button
                    color={viewType === "BAR" ? "primary" : "light"}
                    onClick={() => setViewType("BAR")}
                  >
                    🍺 Bar
                  </Button>
                )}

              </div>

              {/* RIGHT : COUNTS */}
              <div className="d-flex gap-3">
                <div className="text-center">
                  <div className="fw-bold text-success">{openCount}</div>
                  <small className="text-muted">Open</small>
                </div>

                <div className="text-center">
                  <div className="fw-bold text-secondary">{completedCount}</div>
                  <small className="text-muted">Completed</small>
                </div>
              </div>

            </CardBody>
          </Card>

          {/* ===================== ORDERS GRID ===================== */}
          <div className="kds-masonry">
            {filteredOrders.map(order => (
              <div key={order._id} className="kds-masonry-item">
                <Card>
                  <CardBody>

                    {/* HEADER */}
                    <div className="d-flex justify-content-between mb-2">
                      <div>
                        <h6 className="mb-0">{order.orderNo}</h6>
                        <small className="text-muted">
                          {new Date(order.createdAt).toLocaleTimeString("en-IN", {
                            hour: "2-digit",
                            minute: "2-digit"
                          })}
                        </small>
                      </div>
                      {renderStatusBadge(order.status)}
                    </div>

                    <div className="fw-medium mb-2">
                      Table - {order.tableNo}
                    </div>

                    <hr />

                    {/* ITEMS */}
                    {order.items.map((item, idx) => {
                      const itemStatus = item.status || "Waiting";

                      return (
                        <div
                          key={idx}
                          className="d-flex justify-content-between align-items-start mb-3"
                        >
                          <div className="d-flex gap-2">
                            <img
                              src={item.image}
                              alt={item.name}
                              style={{
                                width: 42,
                                height: 42,
                                borderRadius: 6,
                                objectFit: "cover"
                              }}
                            />

                            <div>
                              <div className="fw-medium">
                                {item.quantity} × {item.name}
                              </div>
                              {item.categoryModel === "MenuCategory" && (
                                <small className="text-muted d-block">
                                  Plate: {item.plateType}
                                </small>
                              )}

                              {item.categoryModel === "BarCategory" && (
                                <small className="text-muted d-block">
                                  Type: {item.type}
                                </small>
                              )}

                              {/* ===== COMBINED ADDONS SECTION ===== */}
                              <div className="mt-1">
                                {itemStatus === "Delivered" ? (


                                  <span className="text-success fw-semibold">
                                    ✔ Completed
                                  </span>
                                ) : (
                                  <Button
                                    size="sm"
                                    color={itemStatus === "Waiting" ? "primary" : "success"}
                                    onClick={() =>
                                      handleItemStatus(order._id, item._originalIndex, itemStatus)
                                    }
                                  >
                                    {itemStatus === "Waiting"
                                      ? "Start Preparing"
                                      : "Mark Complete"}
                                  </Button>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}

                    {/* ===== ORDER LEVEL ADDONS (ONE TIME ONLY) ===== */}
                    {order.items.some(i => i.addons?.length > 0) && (
                      <div className="mt-3">

                        {/* dotted separator */}
                        <div
                          style={{
                            borderTop: "2px dashed #adb5bd",
                            margin: "12px 0"
                          }}
                        />

                        <div
                          style={{
                            background: "#f1f3f5",
                            padding: "10px",
                            borderRadius: 6
                          }}
                        >
                          <div
                            style={{
                              fontSize: 14,
                              fontWeight: 600,
                              color: "#495057",
                              marginBottom: 6
                            }}
                          >
                            ADDONS
                          </div>

                          {/* remove duplicates */}
                          {[...new Set(
                            order.items.flatMap(item =>
                              item.addons?.map(a => a.name) || []
                            )
                          )].map((addonName, index) => (
                            <div
                              key={index}
                              style={{
                                fontSize: 14,
                                fontWeight: 500,
                                color: "#0d6efd"
                              }}
                            >
                              + {addonName}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                  </CardBody>
                </Card>
              </div>
            ))}
          </div>
        </Container>
      </div>

    </section>

  );
};

export default KitchenDashboard;






